<script setup>
import { computed, ref } from 'vue';
import { useRouter } from 'vue-router';
import { message } from 'ant-design-vue';
import { MailOutlined, LockOutlined } from '@ant-design/icons-vue';
import { login } from '@/api/auth/login';
import { useAdminInfo } from '@/stores/useAdminInfo';
import { injectPrivateRoutesByPermission } from '@/router/injectPrivateRoutesByPermission';
import {
    goToGrowthAdminSso,
    resolveGrowthAdminSsoBridgeUrl
} from '@/utils/growthSystemNav';

const adminStore = useAdminInfo();
const router = useRouter();
const email = ref('');
const password = ref('');
const emailError = ref(false);
const passwordError = ref(false);
const loginError = ref('');
const loginLoading = ref(false);
const showPasswordLogin = ref(false);

const growthSsoUrl = computed(() =>
    resolveGrowthAdminSsoBridgeUrl(import.meta.env.VITE_GROWTH_SYSTEM_URL)
);
const canUseGrowthSso = computed(() => Boolean(growthSsoUrl.value));

const goGrowthSso = () => {
    loginError.value = '';
    if (!goToGrowthAdminSso(import.meta.env.VITE_GROWTH_SYSTEM_URL)) {
        loginError.value = '尚未設定世界樹文明網址，無法使用世界樹驗證';
        message.error(loginError.value);
    }
};

const validateInputs = () => {
    emailError.value = email.value.trim() === '';
    passwordError.value = password.value.trim() === '';
    return !emailError.value && !passwordError.value;
};

const handleLogin = async () => {
    if (loginLoading.value || !validateInputs()) {
        return;
    }

    loginError.value = '';
    try {
        loginLoading.value = true;
        const response = await login(email.value, password.value);

        if (response.status === 1) {
            message.success('登入成功');
            localStorage.setItem('accessToken', response.data.access_token);
            adminStore.setAdminInfo(response.data.manager);
            localStorage.setItem('lastLoginTime', new Date().toLocaleString());

            try {
                await injectPrivateRoutesByPermission(true);
                router.push(adminStore.defaultHomePath).catch((err) => {
                    console.error('Navigation error:', err);
                });
            } catch (error) {
                console.error('載入權限失敗:', error);
                loginError.value = '載入權限失敗，請重新登入';
                message.error(loginError.value);
            }
        }

        if (response.status === 0) {
            loginError.value = response.message || '信箱或密碼錯誤';
            message.error(loginError.value);
        }
    } catch (error) {
        console.error('Fetching API Error: ', error);
        const apiMessage = error?.response?.data?.message || '';
        const isCredentialError =
            error?.response?.status === 401 ||
            /憑證|credential|信箱或密碼|帳號或密碼/i.test(apiMessage);

        loginError.value =
            error?.code === 'ECONNABORTED'
                ? '登入服務連線逾時，請確認 API 已啟動'
                : isCredentialError
                  ? '信箱或密碼錯誤'
                  : apiMessage || error?.message || '登入失敗';
        message.error(loginError.value);
    } finally {
        loginLoading.value = false;
    }
};

const handleEmailChange = (e) => {
    email.value = e.target.value;
    emailError.value = e.target.value.trim() === '';
    loginError.value = '';
};

const handlePasswordChange = (e) => {
    password.value = e.target.value;
    passwordError.value = e.target.value.trim() === '';
    loginError.value = '';
};
</script>

<template>
    <div class="login-page">
        <div class="login-page__atmosphere" aria-hidden="true">
            <span class="login-page__rule login-page__rule--h" />
            <span class="login-page__rule login-page__rule--v" />
        </div>

        <div class="login-page__stage">
            <header class="login-page__brand">
                <span class="login-page__mark" aria-hidden="true" />
                <h1>資產負債管理後台</h1>
                <p>內部作業系統</p>
            </header>

            <a-card :bordered="false" class="login-page__card">
                <div class="login-page__card-kicker">世界樹驗證</div>
                <div v-if="loginError" class="login-page__alert" role="alert">{{ loginError }}</div>

                <a-button
                    type="primary"
                    size="large"
                    block
                    class="login-page__btn"
                    :disabled="!canUseGrowthSso || loginLoading"
                    @click="goGrowthSso"
                >
                    世界樹驗證
                </a-button>
                <p class="login-page__sso-hint">透過世界樹文明登入</p>

                <button
                    type="button"
                    class="login-page__ops-toggle"
                    :aria-expanded="showPasswordLogin"
                    @click="showPasswordLogin = !showPasswordLogin"
                >
                    {{ showPasswordLogin ? '收合' : '…' }}
                </button>

                <div v-show="showPasswordLogin" class="login-page__ops">
                    <a-form layout="vertical">
                        <a-form-item
                            :validate-status="emailError ? 'error' : ''"
                            :help="emailError ? '信箱不能為空！' : ''"
                        >
                            <a-input
                                :value="email"
                                placeholder="請輸入信箱"
                                size="large"
                                :allow-clear="true"
                                :status="emailError ? 'error' : ''"
                                @change="handleEmailChange"
                                @pressEnter="handleLogin"
                            >
                                <template #prefix>
                                    <MailOutlined
                                        :class="emailError ? 'text-red-500' : 'login-page__icon'"
                                    />
                                </template>
                            </a-input>
                        </a-form-item>

                        <a-form-item
                            :validate-status="passwordError ? 'error' : ''"
                            :help="passwordError ? '密碼不能為空！' : ''"
                        >
                            <a-input-password
                                :value="password"
                                placeholder="請輸入密碼"
                                size="large"
                                :status="passwordError ? 'error' : ''"
                                @change="handlePasswordChange"
                                @pressEnter="handleLogin"
                            >
                                <template #prefix>
                                    <LockOutlined
                                        :class="passwordError ? 'text-red-500' : 'login-page__icon'"
                                    />
                                </template>
                            </a-input-password>
                        </a-form-item>

                        <a-form-item class="login-page__submit">
                            <a-button
                                size="large"
                                block
                                class="login-page__sso"
                                :loading="loginLoading"
                                :disabled="loginLoading"
                                @click="handleLogin"
                            >
                                登入
                            </a-button>
                        </a-form-item>
                    </a-form>
                </div>
            </a-card>

            <p class="login-page__copy">© 2026 資產負債管理後台</p>
        </div>
    </div>
</template>

<style scoped>
.login-page {
    position: relative;
    min-height: 100vh;
    overflow: hidden;
    background:
        radial-gradient(1200px 480px at 80% -10%, rgba(196, 163, 90, 0.16), transparent 55%),
        radial-gradient(900px 420px at -10% 110%, rgba(139, 105, 20, 0.18), transparent 50%),
        linear-gradient(165deg, #1a1c22 0%, #272b36 48%, #16181d 100%);
    color: #e8e4db;
}
.login-page__atmosphere {
    pointer-events: none;
    position: absolute;
    inset: 0;
}
.login-page__rule {
    position: absolute;
    background: linear-gradient(90deg, transparent, rgba(196, 163, 90, 0.22), transparent);
    opacity: 0.7;
}
.login-page__rule--h {
    top: 18%;
    left: 0;
    width: 100%;
    height: 1px;
}
.login-page__rule--v {
    top: 0;
    left: 12%;
    width: 1px;
    height: 100%;
    background: linear-gradient(180deg, transparent, rgba(196, 163, 90, 0.16), transparent);
}
.login-page__stage {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100vh;
    padding: 40px 20px 32px;
}
.login-page__brand {
    margin-bottom: 28px;
    text-align: center;
}
.login-page__mark {
    display: block;
    width: 36px;
    height: 3px;
    margin: 0 auto 16px;
    border-radius: 99px;
    background: linear-gradient(90deg, #d4b56a, #8b6914);
}
.login-page__brand h1 {
    margin: 0;
    color: #f6f1e6;
    font-size: 28px;
    font-weight: 700;
    letter-spacing: 0.12em;
}
.login-page__brand p {
    margin: 8px 0 0;
    color: #c4a35a;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.28em;
}
.login-page__card {
    width: min(400px, 100%);
    border: 1px solid rgba(232, 226, 214, 0.35) !important;
    border-radius: 16px !important;
    background: #fffcf7 !important;
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.28);
}
.login-page__card :deep(.ant-card-body) {
    padding: 28px 28px 22px;
}
.login-page__card-kicker {
    margin-bottom: 18px;
    color: #8b6914;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.08em;
}
.login-page__alert {
    margin: -8px 0 16px;
    padding: 10px 12px;
    border: 1px solid #fecaca;
    border-radius: 10px;
    background: #fef2f2;
    color: #b42318;
    font-size: 13px;
    font-weight: 600;
}
.login-page__icon {
    color: #c4a35a;
}
.login-page__card :deep(.ant-input-affix-wrapper),
.login-page__card :deep(.ant-input-password) {
    height: 44px;
    border-color: #e8e2d6;
    border-radius: 10px;
    background: #fff;
}
.login-page__card :deep(.ant-input-affix-wrapper-focused),
.login-page__card :deep(.ant-input-affix-wrapper:focus) {
    border-color: #c4a35a;
    box-shadow: 0 0 0 3px rgba(196, 163, 90, 0.16);
}
.login-page__submit {
    margin-bottom: 0;
    padding-top: 6px;
}
.login-page__btn.ant-btn-primary {
    height: 44px;
    border-color: #8b6914;
    border-radius: 10px;
    color: #fffdf8;
    background: linear-gradient(180deg, #d4b56a 0%, #8b6914 100%);
    font-weight: 700;
    letter-spacing: 0.12em;
    box-shadow: 0 8px 18px rgba(139, 105, 20, 0.28);
}
.login-page__btn.ant-btn-primary:hover {
    border-color: #6b5428 !important;
    color: #fffdf8 !important;
    background: linear-gradient(180deg, #c4a35a 0%, #6b5428 100%) !important;
}
.login-page__btn.ant-btn-primary:disabled {
    opacity: 0.45;
}
.login-page__ops-toggle {
    display: block;
    width: max-content;
    margin: 18px 0 0 auto;
    padding: 0;
    border: 0;
    background: transparent;
    color: #c4bbb0;
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.2em;
    line-height: 1;
    cursor: pointer;
    opacity: 0.55;
}
.login-page__ops-toggle:hover {
    color: #9a9184;
    opacity: 0.9;
}
.login-page__ops {
    margin-top: 14px;
}
.login-page__sso.ant-btn {
    height: 44px;
    border: 1.5px solid #c4a35a;
    border-radius: 10px;
    color: #6b5428;
    background: #fff;
    font-weight: 700;
    letter-spacing: 0.08em;
}
.login-page__sso.ant-btn:hover:not(:disabled) {
    border-color: #8b6914 !important;
    color: #4a3a1c !important;
    background: #faf7f1 !important;
}
.login-page__sso.ant-btn:disabled {
    opacity: 0.45;
}
.login-page__sso-hint {
    margin: 10px 0 0;
    color: #9a9184;
    font-size: 12px;
    text-align: center;
}
.login-page__copy {
    margin: 22px 0 0;
    color: rgba(232, 228, 219, 0.45);
    font-size: 12px;
}
@media (max-width: 640px) {
    .login-page__brand h1 {
        font-size: 22px;
        letter-spacing: 0.08em;
    }
}
</style>
